import { formatCurrency } from "@sellar-io/common";
import { useState } from "react";
import { twMerge } from "tailwind-merge";
import { Button, DataButton, Icon } from "@sellar-io/components";
import { ProfilePicture } from "../../components";
import { DietaryDisplayList } from "../../components/dietaryDisplayList/DietaryDisplayList";
import { useBuyerProduct } from "../../hooks/api/v2/product/useBuyerProduct";
import { CollabageddonProductDialog } from "./CollabageddonProductDialog";
import { CollabageddonMode } from "./CollabageddonStore";

export type CollabageddonProduct = {
  id: number;
  price: number;
  AvailabilityTierId: number;
  PriceTierId: number;
  SupplierId: number;
  collab: string;
};

export const CollabageddonProductCard = ({
  collabageddonProduct,
  className,
  onAddToBasket,
  isSelected = false,
  mode,
}: {
  collabageddonProduct: CollabageddonProduct;
  className?: string;
  onClick: () => void;
  onAddToBasket: () => void;
  isSelected?: boolean;
  mode?: CollabageddonMode;
  canAddMore?: boolean;
}) => {
  const [productDialogActive, setProductDialogActive] = useState(false);
  const productQuery = useBuyerProduct(collabageddonProduct.id);
  const product = productQuery.data;
  const supplier = product?.Supplier;
  return (
    <>
      <div className="inline-flex flex-col items-center justify-center self-stretch rounded-t-lg">
        <div className="relative aspect-square w-full overflow-hidden rounded-t-lg">
          <img
            className="h-full w-full rounded-t-lg object-cover"
            src={product?.Parent?.imageUrl ?? ""}
          />
        </div>
        <div
          className={twMerge(
            "flex w-full flex-col items-start justify-start gap-2 self-stretch rounded-b-lg bg-white p-4",
            className,
          )}
        >
          {/* Header Section - Supplier Info */}
          <div className="mb-2 flex items-center gap-2">
            {supplier?.Business.logoUrl && (
              <ProfilePicture
                logoUrl={supplier.Business.logoUrl}
                name={supplier.Business.name}
              />
            )}
            <div className="flex-1">
              <div className="font-['Inter'] text-base leading-normal font-semibold text-zinc-800">
                {supplier?.Business.name}
              </div>
              <div className="font-['Inter'] text-xs leading-none font-normal text-zinc-500">
                {collabageddonProduct.collab}
              </div>
            </div>
          </div>

          {/* Product Details */}
          <div className="mb-2 w-full">
            <div className="mb-1 flex items-center gap-2 font-['Inter'] text-lg leading-7 font-semibold text-zinc-800">
              <div className="min-w-0 flex-1 truncate">{product?.name}</div>
              <DietaryDisplayList
                dietaryRequirements={{
                  VG: Boolean(product?.vegan),
                  GF: Boolean(product?.glutenFree),
                  LF: Boolean(product?.lactoseFree),
                }}
                mode="text"
              />
            </div>
            <div className="font-['Inter'] text-sm leading-tight font-normal text-zinc-500">
              {product?.Parent?.kind}
              {" // "}
              {product?.Parent?.abv}% • {product?.containerType || "keg"}
            </div>
          </div>

          {/* Pricing */}
          <div className="mb-2 flex w-full items-center justify-between rounded bg-neutral-50 p-2">
            <div>
              <div className="font-['Inter'] text-sm leading-tight font-semibold text-zinc-800">
                Unit price
              </div>
              <div className="font-['Inter'] text-xs leading-none font-normal text-zinc-700">
                (ex. VAT)
              </div>
            </div>
            <div className="font-['Inter'] text-xl leading-loose font-semibold text-zinc-800">
              {formatCurrency(collabageddonProduct.price)}
            </div>
          </div>

          {/* Buttons Section */}
          <div className="flex flex-col items-start justify-start gap-2 self-stretch">
            {mode === "six" && isSelected ? (
              <>
                {/* View Details Button */}
                <Button
                  color="tertiary"
                  onClick={(e) => {
                    e.stopPropagation();
                    // Handle view details action
                  }}
                  className="w-full self-stretch"
                >
                  <Icon icon="EYE" />
                  View details
                </Button>

                {/* Added state with inline Remove button */}
                <div className="flex w-full items-center gap-2">
                  <Button color={"tertiary"} className={"w-full"}>
                    <Icon icon="CHECK" />
                    Added
                  </Button>
                  <DataButton
                    className={"!w-full text-red-500"}
                    border={true}
                    onClick={(e) => {
                      e.stopPropagation();
                      onAddToBasket();
                    }}
                  >
                    Remove
                  </DataButton>
                </div>
              </>
            ) : mode === "all" ? (
              <>
                {/* View Details Button */}
                <Button
                  color="tertiary"
                  onClick={(e) => {
                    e.stopPropagation();
                    setProductDialogActive(true);
                    // Handle view details action
                  }}
                  className="!w-full self-stretch"
                >
                  <Icon icon="EYE" />
                  View details
                </Button>

                {/* Added state for all mode */}
                <div className="flex w-full items-center justify-center gap-1">
                  <Button color={"tertiary"} className={"w-full"}>
                    <Icon icon="CHECK" />
                    Added
                  </Button>
                </div>
              </>
            ) : (
              <>
                {/* View Details Button */}
                <Button
                  color="tertiary"
                  onClick={(e) => {
                    e.stopPropagation();
                    setProductDialogActive(true);
                    // Handle view details action
                  }}
                  className="!w-full self-stretch"
                >
                  <Icon icon="EYE" />
                  View details
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
      {product?.ParentId && (
        <CollabageddonProductDialog
          active={productDialogActive}
          onDismiss={() => setProductDialogActive(false)}
          ParentId={product.ParentId}
          ProductItemId={collabageddonProduct.id}
          setBestPricePromiseDialogActive={() => setProductDialogActive(true)}
          price={collabageddonProduct.price}
        />
      )}
    </>
  );
};
